/**
 * PWA Phase 3 — client-side first-party signal collector.
 *
 * Zero-friction (no permission prompts): device/network/context + behavioral events,
 * POSTed to /api/pwa/track. Anonymous, session-scoped (a random id in sessionStorage).
 * Best-effort: every call is wrapped so tracking can never throw into the app, and the
 * device snapshot is sent at most ONCE per session (sessionStorage guard).
 */

const SID_KEY = 'spire_pwa_sid'
const SENT_KEY = 'spire_pwa_metrics_sent'

function ssGet(k: string): string | null {
  try { return sessionStorage.getItem(k) } catch { return null }
}
function ssSet(k: string, v: string): void {
  try { sessionStorage.setItem(k, v) } catch { /* private mode / disabled */ }
}

/** Stable per-session anonymous id (not a cookie, not cross-session). */
function sessionId(): string {
  let id = ssGet(SID_KEY)
  if (!id) {
    id = (crypto?.randomUUID?.() ?? `s_${Date.now()}_${Math.round(Math.random() * 1e9)}`)
    ssSet(SID_KEY, id)
  }
  return id
}

function isStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
  } catch { return false }
}

function displayMode(): string {
  return isStandalone() ? 'standalone' : 'browser'
}

function platform(): string {
  const ua = navigator.userAgent
  if (/Android/i.test(ua)) return 'android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Macintosh/i.test(ua)) return 'macos'
  if (/Windows/i.test(ua)) return 'windows'
  return 'other'
}

function send(payload: Record<string, unknown>): void {
  try {
    const body = JSON.stringify(payload)
    // sendBeacon survives page unload; fall back to keepalive fetch.
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/pwa/track', new Blob([body], { type: 'application/json' }))
    } else {
      void fetch('/api/pwa/track', { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } })
    }
  } catch { /* best-effort */ }
}

/** Send the one-per-session device/context snapshot (no-op if already sent). */
export function collectDeviceMetrics(): void {
  if (typeof window === 'undefined') return
  if (ssGet(SENT_KEY)) return
  ssSet(SENT_KEY, '1')

  try {
    const conn = (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; saveData?: boolean } }).connection
    const params = new URLSearchParams(window.location.search)
    send({
      sessionId: sessionId(),
      metrics: {
        displayMode: displayMode(),
        platform: platform(),
        isStandalone: isStandalone(),
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        net: conn ? { effectiveType: conn.effectiveType, downlink: conn.downlink, saveData: conn.saveData } : {},
        deviceMemory: (navigator as unknown as { deviceMemory?: number }).deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        screen: { w: window.screen?.width, h: window.screen?.height, dpr: window.devicePixelRatio },
        referrer: document.referrer || null,
        utm: {
          source: params.get('utm_source'),
          medium: params.get('utm_medium'),
          campaign: params.get('utm_campaign'),
        },
        installState: isStandalone() ? 'installed' : 'web',
      },
    })
  } catch { /* best-effort */ }
}

/** Fire-and-forget behavioral/funnel event. */
export function trackPwaEvent(name: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  send({
    sessionId: sessionId(),
    events: [{
      name,
      data: data ?? {},
      path: window.location.pathname,
      displayMode: displayMode(),
    }],
  })
}
